import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { CommonFooter } from '@/components/layout'
import { IconBox } from '@/components/IconBox'

import homeBtc from '@/assets/images/home-btc.png'
import homeEth from '@/assets/images/home-eth.png'
import homeTotoro from '@/assets/images/home-totoro.png'
import homeMoney from '@/assets/images/home-money.png'
import homeTp from '@/assets/images/home-tp.png'
import homeCh1 from '@/assets/images/home-ch1.png'
import homeCh2 from '@/assets/images/home-ch2.png'
import homeCh3 from '@/assets/images/home-ch3.png'
import mobileHomeTotoro from '@/assets/images/mobile-home-totoro.png'
import mobileHomeMoney from '@/assets/images/mobile-home-money.png'
import mobileHomeTp from '@/assets/images/mobile-home-tp.png'
import mobileHomeCh1 from '@/assets/images/mobile-home-ch1.png'
import mobileHomeCh2 from '@/assets/images/mobile-home-ch2.png'
import mobileHomeCh3 from '@/assets/images/mobile-home-ch3.png'

// Skip animation only when home itself is the initial landing URL.
// If the user entered on another route (e.g. /swap) and navigates to /, animate normally.
const homeWasEntry =
  typeof window !== 'undefined' &&
  (window.location.pathname === '/' || window.location.pathname === '')
let homeHasMounted = false

export function HomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()

  const [animate] = useState(() => {
    const skip = homeWasEntry && !homeHasMounted
    homeHasMounted = true
    return !skip
  })

  return (
    <div
      className={cn(
        'w-full min-h-[calc(100vh-56px)] md:min-h-[calc(100vh-80px)] px-0 md:px-[67px]',
        animate && 'animate-trade-enter',
      )}
      style={{ background: 'linear-gradient(0deg, #fdf8ef 0%, #ecf5ec 100%)' }}
    >
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row items-center md:items-start max-w-[1150px] mx-auto pt-8 md:pt-[81px] md:pl-[137px]">
        <div className="relative text-center md:text-left">
          <h1 className="text-[28px] md:text-[44px] font-extrabold text-[#4b9f47] leading-[40px] md:leading-[64px] whitespace-pre-line">
            {t('home.home1')}
          </h1>
          <p className="text-[14px] md:text-[16px] text-[#666] mt-4 whitespace-pre-line leading-[24px] md:leading-[26px] px-[50px] md:px-0 md:w-[460px]">
            {isMobile ? t('home.home10') : t('home.home2')}
          </p>
          <button
            onClick={() => navigate({ to: '/swap' })}
            className="mx-auto md:mx-0 mt-4 w-[66px] md:w-[78px] h-[32px] md:h-[38px] rounded-full border border-[#4b9f47] flex items-center justify-center bg-transparent hover:bg-[#4b9f47] transition-colors group"
          >
            <ArrowRight className="h-5 w-5 text-[#4b9f47] group-hover:text-white group-hover:translate-x-[5px] transition-all" />
          </button>
        </div>
        <div
          className="md:ml-[136px] mt-4 md:mt-0 w-[218px] md:w-[300px] h-[191px] md:h-[257px] relative"
          style={{
            backgroundImage: `url(${isMobile ? mobileHomeTotoro : homeTotoro})`,
            backgroundSize: '100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <img
            src={homeBtc}
            className="absolute w-[41px] md:w-[49px] h-[41px] md:h-[49px] animate-img-scale top-[-10px] md:top-0 left-[-25px] md:left-0"
          />
          <img
            src={homeEth}
            className="absolute w-[41px] md:w-[50px] h-[40px] md:h-[50px] right-0 md:right-[5px] bottom-[100px] md:bottom-[118px] animate-img-scale2"
          />
        </div>
      </div>

      {/* Low Fees Section */}
      <div
        className="w-full max-w-[1070px] mx-auto mt-[152px] md:mt-[80px] rounded-[60px] flex flex-col md:flex-row items-center md:gap-[40px] md:px-[50px] md:py-[40px] relative"
        style={{ background: 'linear-gradient(0deg, rgba(241,192,99,0.098) 10%, rgba(236,245,236,0.4) 100%)' }}
      >
        <picture>
          <source media="(min-width: 750px)" srcSet={homeMoney} />
          <img src={mobileHomeMoney} className="w-[203px] md:w-[261px] h-auto md:h-[200px] mt-[-90px] md:mt-0 object-contain shrink-0" />
        </picture>
        <div className="text-center md:text-left">
          <h2 className="text-[22px] md:text-[34px] font-extrabold text-foreground mb-2">
            {isMobile ? t('home.home11') : t('home.home3')}
          </h2>
          <p className="text-[14px] md:text-[16px] text-[#666]">
            {isMobile ? t('home.home12') : t('home.home4')}
          </p>
        </div>
      </div>

      {/* Multi-Wallet Section */}
      <div
        className="w-full max-w-[1070px] mx-auto mt-[192px] md:mt-[80px] rounded-[60px] flex flex-col md:flex-row items-center md:gap-[40px] md:px-[50px] md:py-[40px] relative"
        style={{ background: 'linear-gradient(180deg, rgba(241,192,99,0.098) 0%, rgba(236,245,236,0.4) 100%)' }}
      >
        <picture className="md:order-2">
          <source media="(min-width: 750px)" srcSet={homeTp} />
          <img src={mobileHomeTp} className="w-[171px] md:w-[233px] h-auto md:h-[200px] mt-[-105px] md:mt-0 object-contain shrink-0" />
        </picture>
        <div className="text-center md:text-left">
          <h2 className="text-[22px] md:text-[34px] font-extrabold text-foreground mb-2">{t('home.home5')}</h2>
          <p className="text-[14px] md:text-[16px] text-[#666]">
            {isMobile ? t('home.home13') : t('home.home6')}
          </p>
        </div>
      </div>

      {/* Feature Cards */}
      <ul className="flex flex-col md:flex-row items-center justify-center mt-[135px] gap-5 md:gap-[20px]">
        {[
          { img: isMobile ? mobileHomeCh1 : homeCh1, title: t('home.card1Title'), desc: t('home.card1Desc'), to: '/swap' },
          { img: isMobile ? mobileHomeCh2 : homeCh2, title: t('home.card2Title'), desc: t('home.card2Desc'), to: '/pool' },
          { img: isMobile ? mobileHomeCh3 : homeCh3, title: t('home.card3Title'), desc: t('home.card3Desc'), to: '/lend' },
        ].map((card, i) => (
          <li
            key={i}
            onClick={() => navigate({ to: card.to })}
            className="w-[311px] md:w-[330px] bg-white rounded-[26px] border border-[#E5E5E5] hover:border-primary cursor-pointer flex flex-col transition-colors overflow-hidden"
          >
            <img src={card.img} className="w-full h-[156px] md:h-[180px] object-cover" />
            <div className="px-[31px] pt-[20px] pb-[30px] flex flex-col gap-[8px]">
              <span className="text-[18px] md:text-[24px] font-bold text-foreground">
                {card.title}
              </span>
              <p className="text-[12px] md:text-[14px] text-[#666] leading-[1.5]">
                {card.desc}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <CommonFooter />
    </div>
  )
}
